import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import "./Modal.css";

export interface ModalProps {
  open: boolean;
  /** Render as page content, without a backdrop, portal or body scroll lock. */
  inline?: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** picture or round icon above the title, e.g. a coin */
  icon?: ReactNode;
  /** text under the title */
  description?: ReactNode;
  /** buttons row at the bottom */
  actions?: ReactNode;
  /** sm = 400px, md = 520px (default), wide = 640px (tabbed settings),
      lg = 928px */
  size?: "sm" | "md" | "wide" | "lg";
  /** "row" puts buttons side by side, equal widths (default); "stack" puts
      each on its own full-width line, for long button labels; "end" keeps
      their natural width on the right (forms and settings) */
  actionsLayout?: "row" | "stack" | "end";
  /**
   * true (default): X button, Escape and a click outside all call onClose.
   * false: the person must pick one of the actions.
   */
  dismissible?: boolean;
  /** label for the X button, for screen readers */
  closeLabel?: string;
  /** "center" (default) for short messages, "start" for forms */
  align?: "center" | "start";
  /** id on the dialog, e.g. for the product tour */
  id?: string;
  /** "ink" puts the title on a dark band at the top (invitations, choices) */
  tone?: "default" | "ink";
  /** small line above the title, e.g. an avatar with who sent the invite */
  eyebrow?: ReactNode;
  /** shows a Back button above the title (steps inside one popup) */
  onBack?: () => void;
  backLabel?: string;
  children?: ReactNode;
}

/**
 * Centered dialog: title, text, content, buttons.
 * When dismissible, closes on Escape, on the X and on a click outside.
 */
const Modal = ({
  open,
  inline = false,
  onClose,
  title,
  icon,
  description,
  actions,
  size = "md",
  actionsLayout = "row",
  dismissible = true,
  closeLabel = "Close",
  align = "center",
  id,
  tone = "default",
  eyebrow,
  onBack,
  backLabel = "Back",
  children,
}: ModalProps) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Pages often pass a new onClose function on every render; keep the latest
  // one in a ref so the effect below runs only when the popup opens or closes.
  // (Re-running it moved focus away from the field being typed in.)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open || inline) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissibleRef.current) {
        onCloseRef.current();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, inline]);

  if (!open) return null;

  const heading = (
    <>
      {onBack && (
        <Button
          variant="link"
          size="sm"
          className="ds-modal__back"
          iconStart={<Icon name="chevronLeft" size={16} />}
          onClick={onBack}
        >
          {backLabel}
        </Button>
      )}
      {icon && <div className="ds-modal__icon">{icon}</div>}
      {eyebrow && <div className="ds-modal__eyebrow">{eyebrow}</div>}
      {title && (
        <h2 id={titleId} className="ds-modal__title">
          {title}
        </h2>
      )}
      {description && <p className="ds-modal__text">{description}</p>}
    </>
  );

  const body = (
    <>
      {children}
      {actions && (
        <div
          className={`ds-modal__actions ds-modal__actions--${actionsLayout}`}
        >
          {actions}
        </div>
      )}
    </>
  );

  if (inline) {
    return (
      <section
        id={id}
        className={`ds-modal ds-modal--inline ds-modal--${size} ds-modal--${align}`}
      >
        {heading}
        {body}
      </section>
    );
  }

  return createPortal(
    <div
      className="ds-modal-backdrop"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        id={id}
        className={`ds-modal ds-modal--${size} ds-modal--${align} ds-modal--${tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {dismissible && (
          <button
            type="button"
            className="ds-modal__close"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
        {tone === "ink" ? (
          <>
            <div className="ds-modal__head">{heading}</div>
            <div className="ds-modal__body">{body}</div>
          </>
        ) : (
          <>
            {heading}
            {body}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
